import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend, LineChart, Line } from "recharts";
import { DownloadSimpleIcon, ReceiptIcon, CalendarBlankIcon, WalletIcon, ForkKnifeIcon, PackageIcon, ChartLineIcon, MartiniIcon, BedIcon, BriefcaseIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const COLORS = ["#2D4C3B", "#D26B53", "#D49A44", "#3A6349", "#4A6B7C"];

function firstOfMonth() { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); }
function today() { return new Date().toISOString().slice(0, 10); }

function downloadWithAuth(url, filename) {
  const token = localStorage.getItem("pms_token");
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob())
    .then((blob) => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    })
    .catch(() => toast.error("Download failed"));
}

export default function Reports() {
  const { activeId, active } = useProperty();
  const [byProp, setByProp] = useState([]);
  const [overview, setOverview] = useState(null);
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  const [gst, setGst] = useState(null);
  const [scope, setScope] = useState("this"); // 'this' | 'all'

  useEffect(() => {
    const params = {};
    if (activeId) params.property_id = activeId;
    api.get("/reports/occupancy-by-property").then((r) => setByProp(r.data));
    api.get("/reports/overview", { params }).then((r) => setOverview(r.data));
  }, [activeId]);

  const runGst = () => {
    const params = { start, end };
    if (scope === "this" && activeId) params.property_id = activeId;
    api.get("/reports/gst", { params }).then((r) => setGst(r.data));
  };
  useEffect(() => { runGst(); /* eslint-disable-next-line */ }, [start, end, scope, activeId]);

  const exportGstCsv = () => {
    const url = `${API}/reports/gst?start=${start}&end=${end}&format=csv${scope === "this" && activeId ? `&property_id=${activeId}` : ""}`;
    downloadWithAuth(url, `gst-${start}-to-${end}.csv`);
  };
  const exportGstPdf = () => {
    // Simple browser-side PDF via print of a hidden print view
    const url = `/reports/gst/print?start=${start}&end=${end}${scope === "this" && activeId ? `&property_id=${activeId}` : ""}`;
    window.open(url, "_blank");
  };
  const exportRes = () => {
    const url = `${API}/reports/reservations?start=${start}&end=${end}&format=csv${scope === "this" && activeId ? `&property_id=${activeId}` : ""}`;
    downloadWithAuth(url, `reservations-${start}-to-${end}.csv`);
  };

  return (
    <div>
      <PageHeader overline="Analytics" title="Reports" description="Portfolio KPIs, GST breakdown, expense & POS analytics and CSV/PDF exports for your CA and management." />

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card className="p-6"><p className="label-overline">Revenue MTD</p><p className="font-display text-3xl mt-2">{inr(overview?.revenue_mtd)}</p></Card>
        <Card className="p-6"><p className="label-overline">ADR</p><p className="font-display text-3xl mt-2">{inr(overview?.adr)}</p></Card>
        <Card className="p-6"><p className="label-overline">Occupancy</p><p className="font-display text-3xl mt-2">{overview?.occupancy_pct ?? 0}%</p></Card>
        <Card className="p-6"><p className="label-overline">Available</p><p className="font-display text-3xl mt-2">{overview?.available ?? 0}</p></Card>
      </div>

      <Tabs defaultValue="gst" className="space-y-6">
        <TabsList data-testid="reports-tabs">
          <TabsTrigger value="gst" data-testid="reports-tab-gst"><ReceiptIcon size={14} className="mr-1" /> GST</TabsTrigger>
          <TabsTrigger value="managers" data-testid="reports-tab-managers"><BriefcaseIcon size={14} className="mr-1" /> Manager&apos;s Report</TabsTrigger>
          <TabsTrigger value="innkey" data-testid="reports-tab-innkey"><ChartLineIcon size={14} className="mr-1" /> Revenue Matrix</TabsTrigger>
          <TabsTrigger value="reservations" data-testid="reports-tab-reservations"><BedIcon size={14} className="mr-1" /> Reservations</TabsTrigger>
          <TabsTrigger value="daily" data-testid="reports-tab-daily"><CalendarBlankIcon size={14} className="mr-1" /> Daily Sales</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="reports-tab-expenses"><WalletIcon size={14} className="mr-1" /> Expenses</TabsTrigger>
          <TabsTrigger value="pos" data-testid="reports-tab-pos"><ForkKnifeIcon size={14} className="mr-1" /> Restaurant POS</TabsTrigger>
          <TabsTrigger value="bar" data-testid="reports-tab-bar"><MartiniIcon size={14} className="mr-1" /> Bar Reconciliation</TabsTrigger>
          <TabsTrigger value="stock" data-testid="reports-tab-stock"><PackageIcon size={14} className="mr-1" /> Stock</TabsTrigger>
          <TabsTrigger value="portfolio" data-testid="reports-tab-portfolio"><ChartLineIcon size={14} className="mr-1" /> Portfolio</TabsTrigger>
        </TabsList>

        <TabsContent value="daily">
          <DailySalesTab activeId={activeId} scope={scope} />
        </TabsContent>
        <TabsContent value="reservations">
          <ReservationsReportPanel activeId={activeId} scope={scope} setScope={setScope} start={start} end={end} setStart={setStart} setEnd={setEnd} activeName={active?.name} />
        </TabsContent>
        <TabsContent value="managers">
          <ManagersReportPanel activeId={activeId} scope={scope} setScope={setScope} start={start} end={end} setStart={setStart} setEnd={setEnd} activeName={active?.name} />
        </TabsContent>
        <TabsContent value="innkey">
          <InnkeyRevenuePanel activeId={activeId} activeName={active?.name} />
        </TabsContent>
        <TabsContent value="bar">
          <BarReconciliationTab activeId={activeId} />
        </TabsContent>

        <TabsContent value="gst">
      {/* GST report */}
      <Card className="p-6 mb-8">
        <div className="flex flex-wrap justify-between items-end gap-4 mb-4">
          <div>
            <p className="label-overline">Tax</p>
            <h3 className="font-display text-2xl mt-1">GST Report</h3>
            <p className="text-xs text-muted-foreground">CGST + SGST split for intra-state (Sikkim) hospitality invoices.</p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div><Label className="text-xs">From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="gst-start" /></div>
            <div><Label className="text-xs">To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="gst-end" /></div>
            <div>
              <Label className="text-xs">Scope</Label>
              <select value={scope} onChange={(e) => setScope(e.target.value)} className="h-10 rounded-md border border-input bg-background px-2 text-sm" data-testid="gst-scope">
                <option value="this">{active?.name || "Current property"}</option>
                <option value="all">All properties</option>
              </select>
            </div>
            <Button variant="outline" size="sm" onClick={exportGstCsv} data-testid="gst-csv-btn"><DownloadSimpleIcon size={14} className="mr-1" />CSV</Button>
            <Button variant="outline" size="sm" onClick={exportGstPdf} data-testid="gst-pdf-btn"><ReceiptIcon size={14} className="mr-1" />PDF</Button>
            <Button variant="outline" size="sm" onClick={exportRes} data-testid="reservations-csv-btn"><CalendarBlankIcon size={14} className="mr-1" />Reservations CSV</Button>
          </div>
        </div>

        {gst?.summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div className="rounded-md border border-border p-3"><p className="label-overline">Invoices</p><p className="font-mono-plex text-lg mt-1">{gst.summary.total_invoices}</p></div>
            <div className="rounded-md border border-border p-3"><p className="label-overline">Taxable</p><p className="font-mono-plex text-lg mt-1">{inr(gst.summary.taxable_total)}</p></div>
            <div className="rounded-md border border-border p-3"><p className="label-overline">CGST</p><p className="font-mono-plex text-lg mt-1">{inr(gst.summary.cgst_total)}</p></div>
            <div className="rounded-md border border-border p-3"><p className="label-overline">SGST</p><p className="font-mono-plex text-lg mt-1">{inr(gst.summary.sgst_total)}</p></div>
            <div className="rounded-md border border-primary/30 bg-primary/5 p-3"><p className="label-overline text-primary">Gross</p><p className="font-mono-plex text-lg mt-1 text-primary">{inr(gst.summary.gross_total)}</p></div>
          </div>
        )}

        <div className="rounded-md border border-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Property</TableHead>
                <TableHead>Guest</TableHead>
                <TableHead className="text-right">Taxable</TableHead>
                <TableHead className="text-right">CGST</TableHead>
                <TableHead className="text-right">SGST</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(gst?.invoices || []).map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell className="font-mono-plex text-xs">{inv.number}</TableCell>
                  <TableCell className="text-xs">{inv.created_at?.slice(0, 10)}</TableCell>
                  <TableCell className="text-xs">{inv.property_name}</TableCell>
                  <TableCell className="text-xs">{inv.guest_name}</TableCell>
                  <TableCell className="text-right font-mono-plex text-xs">{inr(inv.taxable_amount)}</TableCell>
                  <TableCell className="text-right font-mono-plex text-xs">{inr(inv.cgst)}</TableCell>
                  <TableCell className="text-right font-mono-plex text-xs">{inr(inv.sgst)}</TableCell>
                  <TableCell className="text-right font-mono-plex text-xs font-medium">{inr(inv.total)}</TableCell>
                  <TableCell className="text-xs capitalize">{inv.status}</TableCell>
                </TableRow>
              ))}
              {(gst?.invoices || []).length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground text-sm">No invoices in this range.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
        </TabsContent>

        <TabsContent value="expenses">
          <ExpenseReportPanel activeId={activeId} scope={scope} setScope={setScope} start={start} end={end} setStart={setStart} setEnd={setEnd} activeName={active?.name} />
        </TabsContent>

        <TabsContent value="pos">
          <PosReportPanel activeId={activeId} scope={scope} setScope={setScope} start={start} end={end} setStart={setStart} setEnd={setEnd} activeName={active?.name} />
        </TabsContent>

        <TabsContent value="stock">
          <StockReportPanel activeId={activeId} start={start} end={end} setStart={setStart} setEnd={setEnd} activeName={active?.name} />
        </TabsContent>

        <TabsContent value="portfolio">
      {/* Portfolio charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <p className="label-overline">Occupancy comparison</p>
          <h3 className="font-display text-xl mt-1">By property</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byProp} margin={{ top: 10, right: 10, bottom: 30, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" fontSize={11} stroke="hsl(var(--muted-foreground))" angle={-15} textAnchor="end" />
                <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} />
                <Bar dataKey="occupied" name="Occupied" fill="#2D4C3B" radius={[4, 4, 0, 0]} />
                <Bar dataKey="total" name="Total" fill="#E5E5DE" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6">
          <p className="label-overline">Room mix</p>
          <h3 className="font-display text-xl mt-1">Portfolio share</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={byProp} dataKey="total" nameKey="name" outerRadius={90} innerRadius={50}>
                  {byProp.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ------------------- New Report Panels -------------------
function FilterRow({ scope, setScope, start, end, setStart, setEnd, activeName, hideScope }) {
  return (
    <div className="flex flex-wrap items-end gap-3 mb-4">
      <div><Label className="text-xs">From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" /></div>
      <div><Label className="text-xs">To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" /></div>
      {!hideScope && (
        <div>
          <Label className="text-xs">Scope</Label>
          <select value={scope} onChange={(e) => setScope(e.target.value)} className="h-10 rounded-md border border-input bg-background px-2 text-sm">
            <option value="this">{activeName || "Current property"}</option>
            <option value="all">All properties</option>
          </select>
        </div>
      )}
    </div>
  );
}

function ExpenseReportPanel({ activeId, scope, setScope, start, end, setStart, setEnd, activeName }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = { start, end };
    if (scope === "this" && activeId) params.property_id = activeId;
    api.get("/reports/expenses", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [start, end, scope, activeId]);
  if (!data) return null;
  return (
    <Card className="p-6" data-testid="expense-report-panel">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <p className="label-overline">Operations</p>
          <h3 className="font-display text-2xl mt-1">Expense report</h3>
          <p className="text-xs text-muted-foreground">Cash-out tracking with category, vendor, month and payment-mode breakdowns.</p>
        </div>
        <FilterRow {...{ scope, setScope, start, end, setStart, setEnd, activeName }} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        <Card className="p-4"><p className="label-overline">Total spend</p><p className="font-display text-2xl mt-1" data-testid="exp-total">{inr(data.total)}</p></Card>
        <Card className="p-4"><p className="label-overline">Entries</p><p className="font-display text-2xl mt-1">{data.row_count}</p></Card>
        <Card className="p-4"><p className="label-overline">Input GST claimable</p><p className="font-display text-2xl mt-1 text-[hsl(var(--success))]" data-testid="exp-itc">{inr(data.gst_claimable_input)}</p></Card>
        <Card className="p-4"><p className="label-overline">Pending approvals</p><p className="font-display text-2xl mt-1">{(data.by_status.find((s) => s.status === "pending_approval") || {}).count || 0}</p></Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card className="p-5">
          <p className="label-overline mb-2">By category</p>
          <div className="h-64">
            <ResponsiveContainer><BarChart data={data.by_category}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="category" fontSize={10} angle={-25} textAnchor="end" /><YAxis fontSize={10} /><Tooltip /><Bar dataKey="amount" fill="#D26B53" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-5">
          <p className="label-overline mb-2">Monthly trend</p>
          <div className="h-64">
            <ResponsiveContainer><LineChart data={data.by_month}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" fontSize={10} /><YAxis fontSize={10} /><Tooltip /><Line dataKey="amount" stroke="#2D4C3B" strokeWidth={2} /></LineChart></ResponsiveContainer>
          </div>
        </Card>
      </div>
      {data.top_vendors.length > 0 && (
        <div className="mt-6">
          <p className="label-overline mb-2">Top vendors</p>
          <Table>
            <TableHeader><TableRow><TableHead>Vendor</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
            <TableBody>{data.top_vendors.map((v) => <TableRow key={v.vendor}><TableCell>{v.vendor}</TableCell><TableCell className="text-right font-mono-plex">{inr(v.amount)}</TableCell></TableRow>)}</TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}

function PosReportPanel({ activeId, scope, setScope, start, end, setStart, setEnd, activeName }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = { start, end };
    if (scope === "this" && activeId) params.property_id = activeId;
    api.get("/reports/pos", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [start, end, scope, activeId]);
  if (!data) return null;
  return (
    <Card className="p-6" data-testid="pos-report-panel">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <p className="label-overline">Restaurant &amp; Bar</p>
          <h3 className="font-display text-2xl mt-1">POS analytics</h3>
          <p className="text-xs text-muted-foreground">Order volume, GST, discount, top-selling items, and daily revenue trend.</p>
        </div>
        <FilterRow {...{ scope, setScope, start, end, setStart, setEnd, activeName }} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
        <Card className="p-4"><p className="label-overline">Gross</p><p className="font-display text-2xl mt-1" data-testid="pos-gross">{inr(data.gross)}</p></Card>
        <Card className="p-4"><p className="label-overline">Orders</p><p className="font-display text-2xl mt-1">{data.orders_count}</p></Card>
        <Card className="p-4"><p className="label-overline">Avg order</p><p className="font-display text-2xl mt-1">{inr(data.average_order_value)}</p></Card>
        <Card className="p-4"><p className="label-overline">GST</p><p className="font-display text-2xl mt-1">{inr(data.gst)}</p></Card>
        <Card className="p-4"><p className="label-overline">Discount</p><p className="font-display text-2xl mt-1">{inr(data.discount)}</p></Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card className="p-5">
          <p className="label-overline mb-2">Daily revenue</p>
          <div className="h-64">
            <ResponsiveContainer><LineChart data={data.daily_revenue}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="date" fontSize={10} /><YAxis fontSize={10} /><Tooltip /><Line dataKey="revenue" stroke="#D49A44" strokeWidth={2} /></LineChart></ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-5">
          <p className="label-overline mb-2">By order type</p>
          <div className="h-64">
            <ResponsiveContainer><PieChart><Pie data={data.by_order_type} dataKey="revenue" nameKey="type" outerRadius={90} innerRadius={40}>{data.by_order_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Legend /><Tooltip /></PieChart></ResponsiveContainer>
          </div>
        </Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div>
          <p className="label-overline mb-2">Top items by revenue</p>
          <Table>
            <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Revenue</TableHead></TableRow></TableHeader>
            <TableBody>{data.top_items_by_revenue.map((v) => <TableRow key={v.name}><TableCell>{v.name}</TableCell><TableCell className="text-right font-mono-plex">{inr(v.revenue)}</TableCell></TableRow>)}</TableBody>
          </Table>
        </div>
        <div>
          <p className="label-overline mb-2">Top items by qty</p>
          <Table>
            <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Qty sold</TableHead></TableRow></TableHeader>
            <TableBody>{data.top_items_by_qty.map((v) => <TableRow key={v.name}><TableCell>{v.name}</TableCell><TableCell className="text-right font-mono-plex">{v.qty}</TableCell></TableRow>)}</TableBody>
          </Table>
        </div>
      </div>

      {/* Hourly sales heatmap */}
      <Card className="p-5 mt-6" data-testid="pos-hourly-heatmap">
        <p className="label-overline mb-3">Hourly sales — day-of-week heatmap</p>
        <HourlyHeatmap data={data.heatmap || []} />
        <div className="h-40 mt-4">
          <p className="label-overline mb-1">Overall hourly curve (aggregate)</p>
          <ResponsiveContainer>
            <BarChart data={data.hourly_sales || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" fontSize={10} tickFormatter={(h) => `${h}:00`} />
              <YAxis fontSize={10} />
              <Tooltip formatter={(v) => inr(v)} labelFormatter={(h) => `${h}:00`} />
              <Bar dataKey="revenue" fill="#1F4437" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </Card>
  );
}

function HourlyHeatmap({ data }) {
  const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const max = Math.max(1, ...data.map((d) => d.revenue));
  const cellFor = (dow, hour) => (data.find((d) => d.day_of_week === dow && d.hour === hour) || { revenue: 0 });
  const color = (v) => {
    const p = Math.min(1, v / max);
    const a = 0.05 + p * 0.85;
    return `rgba(31, 68, 55, ${a})`;
  };
  return (
    <div className="overflow-x-auto">
      <table className="text-[9px] w-full min-w-[600px]">
        <thead>
          <tr>
            <th className="p-1"></th>
            {Array.from({ length: 24 }).map((_, h) => (
              <th key={h} className="p-1 text-muted-foreground font-normal">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DAYS.map((day, dow) => (
            <tr key={day}>
              <td className="p-1 text-muted-foreground font-medium pr-2 text-right">{day}</td>
              {Array.from({ length: 24 }).map((_, h) => {
                const c = cellFor(dow, h);
                return (
                  <td key={h} className="p-0.5" data-testid={`heatmap-${day}-${h}`}>
                    <div
                      title={`${day} ${h}:00 — ${inr(c.revenue)}`}
                      className="w-full aspect-square rounded-sm border border-border/30"
                      style={{ backgroundColor: c.revenue > 0 ? color(c.revenue) : "transparent" }}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-2">
        <span>Low</span>
        <div className="flex-1 h-2 rounded" style={{ background: "linear-gradient(to right, rgba(31,68,55,0.05), rgba(31,68,55,0.9))" }} />
        <span>High ({inr(max)})</span>
      </div>
    </div>
  );
}

function StockReportPanel({ activeId, start, end, setStart, setEnd, activeName }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!activeId) return;
    api.get("/reports/stock", { params: { property_id: activeId, start, end } }).then((r) => setData(r.data)).catch(() => {});
  }, [start, end, activeId]);
  if (!activeId) return <p className="text-sm text-muted-foreground italic">Pick a property to view its stock report.</p>;
  if (!data) return null;
  return (
    <Card className="p-6" data-testid="stock-report-panel">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <p className="label-overline">Store keeping</p>
          <h3 className="font-display text-2xl mt-1">Stock movement report</h3>
          <p className="text-xs text-muted-foreground">Inventory in/out, reason breakdown, top movers and current valuation for {activeName}.</p>
        </div>
        <FilterRow start={start} end={end} setStart={setStart} setEnd={setEnd} hideScope />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        <Card className="p-4"><p className="label-overline">Total in</p><p className="font-display text-2xl mt-1 text-[hsl(var(--success))]">+{data.total_in}</p></Card>
        <Card className="p-4"><p className="label-overline">Total out</p><p className="font-display text-2xl mt-1 text-destructive">-{data.total_out}</p></Card>
        <Card className="p-4"><p className="label-overline">Net</p><p className="font-display text-2xl mt-1">{data.net}</p></Card>
        <Card className="p-4"><p className="label-overline">Inventory value</p><p className="font-display text-2xl mt-1" data-testid="stock-valuation">{inr(data.current_inventory_valued_at_menu_price)}</p></Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card className="p-5">
          <p className="label-overline mb-2">By reason</p>
          <Table>
            <TableHeader><TableRow><TableHead>Reason</TableHead><TableHead className="text-right">Movements</TableHead><TableHead className="text-right">Net delta</TableHead></TableRow></TableHeader>
            <TableBody>{data.by_reason.map((v) => <TableRow key={v.reason}><TableCell className="text-xs">{v.reason}</TableCell><TableCell className="text-right font-mono-plex">{v.count}</TableCell><TableCell className={`text-right font-mono-plex ${v.delta > 0 ? "text-[hsl(var(--success))]" : "text-destructive"}`}>{v.delta > 0 ? "+" : ""}{v.delta}</TableCell></TableRow>)}</TableBody>
          </Table>
        </Card>
        <Card className="p-5">
          <p className="label-overline mb-2">Top movers</p>
          <Table>
            <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Abs Δ</TableHead><TableHead className="text-right">Net Δ</TableHead></TableRow></TableHeader>
            <TableBody>{data.top_movers.map((v) => <TableRow key={v.name}><TableCell className="text-xs">{v.name}</TableCell><TableCell className="text-right font-mono-plex">{v.abs_delta}</TableCell><TableCell className={`text-right font-mono-plex ${v.net_delta > 0 ? "text-[hsl(var(--success))]" : "text-destructive"}`}>{v.net_delta}</TableCell></TableRow>)}</TableBody>
          </Table>
        </Card>
      </div>
    </Card>
  );
}


function DailySalesTab({ activeId, scope }) {
  const [data, setData] = useState(null);
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  useEffect(() => {
    const params = { start, end };
    if (scope === "this" && activeId) params.property_id = activeId;
    api.get("/reports/daily-sales", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [start, end, activeId, scope]);
  return (
    <Card className="p-6" data-testid="reports-daily-sales">
      <div className="flex flex-wrap justify-between items-end gap-4 mb-4">
        <div>
          <p className="label-overline">Consolidated</p>
          <h3 className="font-display text-2xl mt-1">Daily Sales · all functions</h3>
          <p className="text-xs text-muted-foreground">Rooms, Restaurant POS, Halls/Events, Amenities and Expenses in one grid.</p>
        </div>
        <div className="flex items-end gap-3">
          <div><Label className="text-xs">From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="daily-sales-start" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="daily-sales-end" /></div>
        </div>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
            {[
              ["Rooms", data.totals.rooms],
              ["Restaurant", data.totals.pos],
              ["Halls", data.totals.halls],
              ["Amenities", data.totals.amenities],
              ["Expenses", data.totals.expenses],
              ["Net", data.totals.net],
            ].map(([k, v]) => (
              <Card key={k} className="p-3">
                <p className="label-overline">{k}</p>
                <p className={`font-display text-xl mt-1 ${k === "Net" && v < 0 ? "text-destructive" : ""}`}>{inr(v)}</p>
              </Card>
            ))}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Rooms</TableHead>
                <TableHead className="text-right">Restaurant</TableHead>
                <TableHead className="text-right">Halls</TableHead>
                <TableHead className="text-right">Amenities</TableHead>
                <TableHead className="text-right">Expenses</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow key={r.date} data-testid={`daily-row-${r.date}`}>
                  <TableCell className="text-xs font-mono-plex">{r.date}</TableCell>
                  <TableCell className="text-right font-mono-plex">{inr(r.rooms)}</TableCell>
                  <TableCell className="text-right font-mono-plex">{inr(r.pos)}</TableCell>
                  <TableCell className="text-right font-mono-plex">{inr(r.halls)}</TableCell>
                  <TableCell className="text-right font-mono-plex">{inr(r.amenities)}</TableCell>
                  <TableCell className="text-right font-mono-plex text-destructive">{inr(r.expenses)}</TableCell>
                  <TableCell className="text-right font-mono-plex font-semibold">{inr(r.total)}</TableCell>
                </TableRow>
              ))}
              {data.rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No sales in this window.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </>
      )}
    </Card>
  );
}

function BarReconciliationTab({ activeId }) {
  const [data, setData] = useState(null);
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  useEffect(() => {
    if (!activeId) return;
    api.get("/reports/bar-reconciliation", { params: { property_id: activeId, start, end } }).then((r) => setData(r.data)).catch(() => {});
  }, [start, end, activeId]);
  return (
    <Card className="p-6" data-testid="reports-bar-recon">
      <div className="flex flex-wrap justify-between items-end gap-4 mb-4">
        <div>
          <p className="label-overline">Bar</p>
          <h3 className="font-display text-2xl mt-1">Bar Reconciliation</h3>
          <p className="text-xs text-muted-foreground">Bottles opened vs pegs poured vs pegs remaining. Watch the variance column — negative means shrinkage.</p>
        </div>
        <div className="flex items-end gap-3">
          <div><Label className="text-xs">From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="bar-recon-start" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="bar-recon-end" /></div>
        </div>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            <Card className="p-3"><p className="label-overline">Pegs sold</p><p className="font-display text-xl mt-1">{data.totals.pegs_sold}</p></Card>
            <Card className="p-3"><p className="label-overline">Bottles purchased</p><p className="font-display text-xl mt-1">{data.totals.bottles_purchased}</p></Card>
            <Card className="p-3"><p className="label-overline">Bar revenue</p><p className="font-display text-xl mt-1">{inr(data.totals.revenue)}</p></Card>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead className="text-right">Bottle · Peg</TableHead>
                <TableHead className="text-right">Bottles purchased</TableHead>
                <TableHead className="text-right">Pegs sold</TableHead>
                <TableHead className="text-right">Wasted</TableHead>
                <TableHead className="text-right">Remaining pegs</TableHead>
                <TableHead className="text-right">Variance</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow key={r.menu_item_id} data-testid={`bar-recon-row-${r.menu_item_id}`}>
                  <TableCell>{r.name}</TableCell>
                  <TableCell className="text-right text-xs">{r.bottle_size_ml}ml · {r.peg_size_ml}ml</TableCell>
                  <TableCell className="text-right font-mono-plex">{r.bottles_purchased_in_range}</TableCell>
                  <TableCell className="text-right font-mono-plex">{r.pegs_sold}</TableCell>
                  <TableCell className="text-right font-mono-plex text-destructive">{r.pegs_wasted}</TableCell>
                  <TableCell className="text-right font-mono-plex">{r.pegs_remaining}</TableCell>
                  <TableCell className={`text-right font-mono-plex ${r.variance_pegs && r.variance_pegs < 0 ? "text-destructive" : r.variance_pegs && r.variance_pegs > 0 ? "text-[hsl(var(--success))]" : ""}`}>
                    {r.variance_pegs === null ? "—" : (r.variance_pegs > 0 ? "+" : "") + r.variance_pegs}
                  </TableCell>
                  <TableCell className="text-right font-mono-plex">{inr(r.revenue)}</TableCell>
                </TableRow>
              ))}
              {data.rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No bar items configured. Add "Bar item — sold by peg" items in POS → Menu.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </>
      )}
    </Card>
  );
}


const RES_STATUSES = [
  { id: "booked", label: "Booked" },
  { id: "checked_in", label: "Checked-in" },
  { id: "checked_out", label: "Checked-out" },
  { id: "cancelled", label: "Cancelled" },
  { id: "no_show", label: "No-show" },
];
const CHANNELS = ["direct", "booking_com", "mmt", "yatra", "agoda", "airbnb", "goibibo", "public_widget"];

function ReservationsReportPanel({ activeId, scope, setScope, start, end, setStart, setEnd, activeName }) {
  const [data, setData] = useState(null);
  const [statuses, setStatuses] = useState(["booked", "checked_in", "checked_out"]);
  const [channel, setChannel] = useState("all");

  useEffect(() => {
    const params = { start, end };
    if (scope === "this" && activeId) params.property_id = activeId;
    api.get("/reports/reservations", { params }).then((r) => setData(r.data)).catch(() => setData({ reservations: [], count: 0 }));
  }, [start, end, scope, activeId]);

  const rows = (data?.reservations || []).filter((r) => statuses.includes(r.status) && (channel === "all" || (r.channel || "direct") === channel));
  const totals = rows.reduce((a, r) => {
    a.count += 1;
    a.amount += Number(r.total_amount || 0);
    a.paid += Number(r.paid_amount || 0);
    return a;
  }, { count: 0, amount: 0, paid: 0 });

  const toggleStatus = (id) => setStatuses((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const exportCsv = () => {
    const header = ["Check-in", "Check-out", "Property", "Room", "Guest", "Phone", "Adults", "Children", "Amount", "Paid", "Status", "Channel"];
    const lines = [header.join(",")];
    rows.forEach((r) => {
      const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      lines.push([
        r.check_in, r.check_out,
        (r.property || {}).name || "",
        `${(r.room || {}).number || ""} · ${(r.room || {}).room_type || ""}`,
        (r.guest || {}).name || "",
        (r.guest || {}).phone || "",
        r.adults, r.children,
        r.total_amount, r.paid_amount || 0,
        r.status, r.channel || "direct",
      ].map(cell).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `reservations-${start}-to-${end}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Card className="p-6" data-testid="reservations-report-panel">
      <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
        <div>
          <p className="label-overline">Front desk</p>
          <h3 className="font-display text-2xl mt-1">Reservations report</h3>
          <p className="text-xs text-muted-foreground">Filter by property, date range, status and channel.</p>
        </div>
        <FilterRow {...{ scope, setScope, start, end, setStart, setEnd, activeName }} />
      </div>

      <div className="rounded-md border border-border p-3 mb-4 flex flex-wrap gap-4 items-center" data-testid="reservations-filters">
        <div className="flex flex-wrap gap-3 items-center">
          <p className="label-overline mr-1">Status</p>
          {RES_STATUSES.map((s) => (
            <label key={s.id} className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid={`res-status-${s.id}`}>
              <Checkbox checked={statuses.includes(s.id)} onCheckedChange={() => toggleStatus(s.id)} />
              <span>{s.label}</span>
            </label>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <p className="label-overline mr-1">Channel</p>
          <select value={channel} onChange={(e) => setChannel(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-xs" data-testid="res-channel">
            <option value="all">All channels</option>
            {CHANNELS.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div className="ml-auto">
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="res-csv-btn"><DownloadSimpleIcon size={14} className="mr-1" />Export CSV</Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-md border border-border p-3"><p className="label-overline">Count</p><p className="font-mono-plex text-lg mt-1" data-testid="res-total-count">{totals.count}</p></div>
        <div className="rounded-md border border-border p-3"><p className="label-overline">Booked value</p><p className="font-mono-plex text-lg mt-1" data-testid="res-total-amount">{inr(totals.amount)}</p></div>
        <div className="rounded-md border border-primary/30 bg-primary/5 p-3"><p className="label-overline text-primary">Collected</p><p className="font-mono-plex text-lg mt-1 text-primary" data-testid="res-total-paid">{inr(totals.paid)}</p></div>
      </div>

      <div className="rounded-md border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Check-in</TableHead>
              <TableHead>Check-out</TableHead>
              <TableHead>Property</TableHead>
              <TableHead>Room</TableHead>
              <TableHead>Guest</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`res-row-${r.id}`}>
                <TableCell className="text-xs">{r.check_in}</TableCell>
                <TableCell className="text-xs">{r.check_out}</TableCell>
                <TableCell className="text-xs">{(r.property || {}).name}</TableCell>
                <TableCell className="text-xs">{(r.room || {}).number} · {(r.room || {}).room_type}</TableCell>
                <TableCell className="text-xs">{(r.guest || {}).name}</TableCell>
                <TableCell className="text-xs capitalize">{(r.channel || "direct").replace(/_/g, " ")}</TableCell>
                <TableCell className="text-right font-mono-plex text-xs">{inr(r.total_amount)}</TableCell>
                <TableCell className="text-right font-mono-plex text-xs">{inr(r.paid_amount || 0)}</TableCell>
                <TableCell className="text-xs capitalize">{r.status.replace(/_/g, " ")}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground text-sm">No reservations match these filters.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function ManagersReportPanel({ activeId, scope, setScope, start, end, setStart, setEnd, activeName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = { start, end };
    if (scope === "this" && activeId) params.property_id = activeId;
    setLoading(true);
    api.get("/reports/managers", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [start, end, scope, activeId]);

  const printReport = () => {
    if (!data) return;
    const w = window.open("", "_blank", "width=1100,height=1200");
    if (!w) return toast.error("Popup blocked — allow popups and retry");
    w.document.write(buildManagerHtml(data, activeName));
    w.document.close();
  };

  const t = data?.totals || {};
  const rev = data?.revenue || {};
  const collections = Object.entries(data?.collections || {}).sort((a, b) => b[1] - a[1]);

  return (
    <Card className="p-6" data-testid="managers-report-panel">
      <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
        <div>
          <p className="label-overline">Executive</p>
          <h3 className="font-display text-2xl mt-1">Manager&apos;s Flash Report</h3>
          <p className="text-xs text-muted-foreground">Occupancy, ADR, RevPAR, revenue split, collections and top guests for the selected window. Revenue counts every billed invoice.</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <FilterRow {...{ scope, setScope, start, end, setStart, setEnd, activeName }} />
          <Button variant="outline" size="sm" onClick={printReport} disabled={!data} data-testid="managers-print-btn"><ReceiptIcon size={14} className="mr-1" /> Print</Button>
        </div>
      </div>

      {loading && <p className="text-sm text-muted-foreground py-6">Loading…</p>}

      {data && !loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="managers-kpi">
            <Card className="p-4"><p className="label-overline">Occupancy</p><p className="font-display text-2xl mt-1" data-testid="mgr-occupancy">{t.occupancy_pct}%</p><p className="text-[10px] text-muted-foreground mt-0.5">{t.room_nights_sold} / {t.room_nights_available} nights</p></Card>
            <Card className="p-4"><p className="label-overline">ADR</p><p className="font-display text-2xl mt-1" data-testid="mgr-adr">{inr(t.adr)}</p><p className="text-[10px] text-muted-foreground mt-0.5">Avg room rate</p></Card>
            <Card className="p-4"><p className="label-overline">RevPAR</p><p className="font-display text-2xl mt-1" data-testid="mgr-revpar">{inr(t.revpar)}</p><p className="text-[10px] text-muted-foreground mt-0.5">Rev per avail room</p></Card>
            <Card className="p-4"><p className="label-overline">Total revenue</p><p className="font-display text-2xl mt-1 text-primary" data-testid="mgr-total-rev">{inr(rev.total)}</p><p className="text-[10px] text-muted-foreground mt-0.5">Billed</p></Card>
            <Card className="p-4"><p className="label-overline">Net (after expenses)</p><p className="font-display text-2xl mt-1" data-testid="mgr-net">{inr(rev.net)}</p><p className="text-[10px] text-muted-foreground mt-0.5">Expenses {inr(rev.expenses)}</p></Card>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <Card className="p-4"><p className="label-overline">Arrivals</p><p className="font-display text-2xl mt-1" data-testid="mgr-arrivals">{t.arrivals}</p></Card>
            <Card className="p-4"><p className="label-overline">Departures</p><p className="font-display text-2xl mt-1">{t.departures}</p></Card>
            <Card className="p-4"><p className="label-overline">No-shows</p><p className="font-display text-2xl mt-1">{t.no_shows}</p></Card>
            <Card className="p-4"><p className="label-overline">Cancellations</p><p className="font-display text-2xl mt-1">{t.cancellations}</p></Card>
            <Card className="p-4"><p className="label-overline">Avg length of stay</p><p className="font-display text-2xl mt-1">{t.avg_length_of_stay} <span className="text-sm">nt</span></p></Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="p-5">
              <p className="label-overline">Revenue breakdown</p>
              <Table>
                <TableBody>
                  <TableRow><TableCell className="text-xs">Rooms</TableCell><TableCell className="text-right font-mono-plex text-xs">{inr(rev.rooms)}</TableCell></TableRow>
                  <TableRow><TableCell className="text-xs">POS (direct)</TableCell><TableCell className="text-right font-mono-plex text-xs">{inr(rev.pos_direct)}</TableCell></TableRow>
                  <TableRow><TableCell className="text-xs">POS on folio</TableCell><TableCell className="text-right font-mono-plex text-xs">{inr(rev.pos_on_invoice)}</TableCell></TableRow>
                  <TableRow><TableCell className="text-xs">Halls</TableCell><TableCell className="text-right font-mono-plex text-xs">{inr(rev.halls)}</TableCell></TableRow>
                  <TableRow><TableCell className="text-xs">Amenities</TableCell><TableCell className="text-right font-mono-plex text-xs">{inr(rev.amenities)}</TableCell></TableRow>
                  <TableRow><TableCell className="text-xs">Discounts</TableCell><TableCell className="text-right font-mono-plex text-xs text-[hsl(var(--warning))]">− {inr(rev.discount)}</TableCell></TableRow>
                  <TableRow><TableCell className="text-xs">Tax collected</TableCell><TableCell className="text-right font-mono-plex text-xs">{inr(rev.tax)}</TableCell></TableRow>
                  <TableRow><TableCell className="text-xs">Expenses</TableCell><TableCell className="text-right font-mono-plex text-xs text-[hsl(var(--warning))]">− {inr(rev.expenses)}</TableCell></TableRow>
                  <TableRow className="bg-primary/5"><TableCell className="text-sm font-medium">Net</TableCell><TableCell className="text-right font-mono-plex text-sm font-medium text-primary">{inr(rev.net)}</TableCell></TableRow>
                </TableBody>
              </Table>
            </Card>
            <Card className="p-5">
              <p className="label-overline">Collections by mode</p>
              {collections.length === 0 && <p className="text-sm text-muted-foreground mt-4">No settled payments in this window.</p>}
              {collections.length > 0 && (
                <Table>
                  <TableBody>
                    {collections.map(([mode, amt]) => (
                      <TableRow key={mode} data-testid={`mgr-collect-${mode}`}>
                        <TableCell className="text-xs capitalize">{mode.replace(/_/g, " ")}</TableCell>
                        <TableCell className="text-right font-mono-plex text-xs">{inr(amt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </div>

          {(data.channels || []).length > 0 && (
            <div className="mb-6">
              <p className="label-overline mb-2">Bookings by channel</p>
              <Table>
                <TableHeader><TableRow><TableHead>Channel</TableHead><TableHead className="text-right">Bookings</TableHead><TableHead className="text-right">Booked value</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.channels.map((c) => (
                    <TableRow key={c.channel} data-testid={`mgr-channel-${c.channel}`}>
                      <TableCell className="text-xs capitalize">{c.channel.replace(/_/g, " ")}</TableCell>
                      <TableCell className="text-right font-mono-plex text-xs">{c.bookings}</TableCell>
                      <TableCell className="text-right font-mono-plex text-xs">{inr(c.revenue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {(data.top_guests || []).length > 0 && (
            <div className="mb-6">
              <p className="label-overline mb-2">Top 5 guests by spend</p>
              <Table>
                <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Guest</TableHead><TableHead>Phone</TableHead><TableHead className="text-right">Total spend</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.top_guests.map((g, idx) => (
                    <TableRow key={g.guest_id} data-testid={`mgr-guest-${idx}`}>
                      <TableCell className="text-xs">{idx + 1}</TableCell>
                      <TableCell className="text-xs">{g.name}</TableCell>
                      <TableCell className="text-xs font-mono-plex">{g.phone}</TableCell>
                      <TableCell className="text-right font-mono-plex text-xs">{inr(g.total_spend)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div>
            <p className="label-overline mb-2">Daily breakdown</p>
            <div className="rounded-md border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Arrivals</TableHead>
                    <TableHead className="text-right">Departures</TableHead>
                    <TableHead className="text-right">Nights sold</TableHead>
                    <TableHead className="text-right">Occ %</TableHead>
                    <TableHead className="text-right">Rooms</TableHead>
                    <TableHead className="text-right">POS</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.daily || []).map((d) => (
                    <TableRow key={d.date}>
                      <TableCell className="text-xs">{d.date}</TableCell>
                      <TableCell className="text-right text-xs">{d.arrivals}</TableCell>
                      <TableCell className="text-right text-xs">{d.departures}</TableCell>
                      <TableCell className="text-right text-xs">{d.room_nights_sold}</TableCell>
                      <TableCell className="text-right text-xs">{d.occupancy_pct}%</TableCell>
                      <TableCell className="text-right font-mono-plex text-xs">{inr(d.rooms_revenue)}</TableCell>
                      <TableCell className="text-right font-mono-plex text-xs">{inr(d.pos_revenue)}</TableCell>
                      <TableCell className="text-right font-mono-plex text-xs font-medium">{inr(d.total_revenue)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

function _mgrEsc(v) { return String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])); }
function buildManagerHtml(d, activeName) {
  const t = d.totals || {}; const rev = d.revenue || {};
  const money = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");
  const row = (l, v) => `<tr><td>${_mgrEsc(l)}</td><td class="r">${_mgrEsc(v)}</td></tr>`;
  const collections = Object.entries(d.collections || {}).map(([m, a]) => row(m, money(a))).join("");
  const channels = (d.channels || []).map((c) => `<tr><td>${_mgrEsc(c.channel)}</td><td class="r">${c.bookings}</td><td class="r">${money(c.revenue)}</td></tr>`).join("");
  const guests = (d.top_guests || []).map((g, i) => `<tr><td>${i+1}</td><td>${_mgrEsc(g.name)}</td><td>${_mgrEsc(g.phone)}</td><td class="r">${money(g.total_spend)}</td></tr>`).join("");
  const daily = (d.daily || []).map((r) => `<tr><td>${r.date}</td><td class="r">${r.arrivals}</td><td class="r">${r.departures}</td><td class="r">${r.room_nights_sold}</td><td class="r">${r.occupancy_pct}%</td><td class="r">${money(r.rooms_revenue)}</td><td class="r">${money(r.pos_revenue)}</td><td class="r">${money(r.total_revenue)}</td></tr>`).join("");
  return `<!doctype html><html><head><title>Manager's Report · ${_mgrEsc(activeName || d.property_name || "")} · ${d.start} → ${d.end}</title><meta charset="utf-8"/><style>
    body{font-family:'Inter',system-ui,sans-serif;color:#16241B;margin:20px;font-size:11px}
    h1{font-family:'Playfair Display',serif;color:#2F5D50;margin:0;font-size:22px}
    h2{font-family:'Playfair Display',serif;color:#2F5D50;font-size:13px;text-transform:uppercase;letter-spacing:.08em;border-bottom:1px solid #C8A24B;padding-bottom:3px;margin:14px 0 6px}
    table{width:100%;border-collapse:collapse;font-size:10.5px;margin-bottom:8px}
    th,td{padding:4px 6px;border-bottom:1px solid #E5E5DE;text-align:left}
    th{background:#F5F1E6;font-size:10px}
    td.r,th.r{text-align:right;font-family:'JetBrains Mono',monospace}
    .grid{display:grid;grid-template-columns:1fr 1fr;gap:24px}
    @page{margin:12mm}
  </style></head><body>
  <h1>${_mgrEsc(activeName || d.property_name || "Property")}</h1>
  <p style="color:#4E534D;font-size:10px;margin:2px 0 12px">Manager's Flash Report · ${d.start} → ${d.end} (${d.span_days} day${d.span_days > 1 ? "s" : ""})</p>
  <div class="grid">
    <div>
      <h2>Occupancy</h2>
      <table>
        ${row("Room-nights available", t.room_nights_available ?? 0)}
        ${row("Room-nights sold", t.room_nights_sold ?? 0)}
        ${row("Occupancy", (t.occupancy_pct ?? 0) + "%")}
        ${row("ADR", money(t.adr))}
        ${row("RevPAR", money(t.revpar))}
        ${row("Avg length of stay", (t.avg_length_of_stay ?? 0) + " nt")}
        ${row("Arrivals", t.arrivals ?? 0)}
        ${row("Departures", t.departures ?? 0)}
        ${row("No-shows", t.no_shows ?? 0)}
        ${row("Cancellations", t.cancellations ?? 0)}
      </table>
    </div>
    <div>
      <h2>Revenue</h2>
      <table>
        ${row("Rooms", money(rev.rooms))}
        ${row("POS on folio", money(rev.pos_on_invoice))}
        ${row("POS direct", money(rev.pos_direct))}
        ${row("Halls", money(rev.halls))}
        ${row("Amenities", money(rev.amenities))}
        ${row("Discounts", "− " + money(rev.discount))}
        ${row("Tax collected", money(rev.tax))}
        ${row("Expenses", "− " + money(rev.expenses))}
        <tr style="background:#F5F1E6"><td><b>Total revenue</b></td><td class="r"><b>${money(rev.total)}</b></td></tr>
        <tr><td><b>Net</b></td><td class="r"><b>${money(rev.net)}</b></td></tr>
      </table>
    </div>
  </div>
  <h2>Collections by mode</h2><table>${collections || `<tr><td colspan="2" style="color:#4E534D">No settled payments.</td></tr>`}</table>
  ${channels ? `<h2>Bookings by channel</h2><table><tr><th>Channel</th><th class="r">Bookings</th><th class="r">Booked value</th></tr>${channels}</table>` : ""}
  ${guests ? `<h2>Top 5 guests</h2><table><tr><th>#</th><th>Guest</th><th>Phone</th><th class="r">Spend</th></tr>${guests}</table>` : ""}
  <h2>Daily breakdown</h2>
  <table><tr><th>Date</th><th class="r">Arr</th><th class="r">Dep</th><th class="r">Nights</th><th class="r">Occ%</th><th class="r">Rooms</th><th class="r">POS</th><th class="r">Total</th></tr>${daily}</table>
  <p style="margin-top:20px;text-align:right;color:#4E534D;font-size:9px;text-transform:uppercase;letter-spacing:.05em">Printed ${_mgrEsc(new Date().toLocaleString("en-IN"))}</p>
  <script>window.addEventListener('load', () => setTimeout(() => { window.focus(); window.print(); }, 300));</script>
  </body></html>`;
}


function InnkeyRevenuePanel({ activeId, activeName }) {
  const [data, setData] = useState(null);
  const [asOf, setAsOf] = useState(today());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeId) return;
    const params = { property_id: activeId, as_of: asOf };
    setLoading(true);
    api.get("/reports/innkey-revenue", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [activeId, asOf]);

  const printReport = () => {
    if (!data) return;
    const w = window.open("", "_blank", "width=1200,height=1400");
    if (!w) return toast.error("Popup blocked — allow popups and retry");
    w.document.write(buildInnkeyHtml(data, activeName));
    w.document.close();
  };

  const cols = [
    { key: "yesterday", label: "Yesterday" },
    { key: "wtd", label: "Week To Date" },
    { key: "mtd", label: "Month To Date" },
    { key: "ytd", label: "Year To Date" },
  ];

  const cell = (v) => v == null ? <span className="text-muted-foreground">—</span> : <span className="font-mono-plex">{inr(v)}</span>;
  const renderRow = (label, group, path, kind) => {
    const isTotal = kind === "total";
    const isSection = kind === "section";
    const cls = isSection
      ? "bg-primary/10 text-primary uppercase tracking-widest text-[10px] font-medium"
      : isTotal
      ? "bg-muted/50 font-medium"
      : "";
    return (
      <TableRow key={`${label}-${group || ""}-${path || ""}`} className={cls} data-testid={`innkey-row-${path || group || label}`}>
        <TableCell className={isSection ? "pl-3" : "pl-6"}>{label}</TableCell>
        {cols.map((c) => {
          if (isSection) return <TableCell key={c.key} />;
          const bucket = data?.[c.key]?.[group];
          const val = bucket && path ? bucket[path] : (bucket ? bucket.total : null);
          return <TableCell key={c.key} className="text-right">{cell(val)}</TableCell>;
        })}
      </TableRow>
    );
  };

  return (
    <Card className="p-0 overflow-hidden" data-testid="innkey-revenue-panel">
      <div className="p-6 flex items-start justify-between gap-4 border-b border-border flex-wrap">
        <div>
          <p className="label-overline">Executive · InnKey style</p>
          <h3 className="font-display text-2xl mt-1">Revenue Matrix</h3>
          <p className="text-xs text-muted-foreground">F&amp;B and Front Office revenue split across Yesterday · WTD · MTD · YTD. Money counts every billed invoice.</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs">As of</Label>
            <Input type="date" value={asOf} max={today()} onChange={(e) => setAsOf(e.target.value)} className="w-44 mt-1" data-testid="innkey-asof" />
          </div>
          <Button variant="outline" size="sm" onClick={printReport} disabled={!data} data-testid="innkey-print-btn"><ReceiptIcon size={14} className="mr-1" /> Print</Button>
        </div>
      </div>

      {loading && <p className="text-sm text-muted-foreground p-6">Loading…</p>}

      {data && !loading && (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[hsl(var(--secondary))]/20">
                <TableHead className="w-64">Head</TableHead>
                {cols.map((c) => <TableHead key={c.key} className="text-right">{c.label}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {renderRow("F&B Service", null, null, "section")}
              {renderRow("RESTAURANT REVENUE", null, null, "section")}
              {renderRow("Food Revenue", "restaurant", "food")}
              {renderRow("Bar", "restaurant", "bar")}
              {renderRow("Total Restaurant Revenue", "restaurant", null, "total")}

              {renderRow("IRD REVENUE", null, null, "section")}
              {renderRow("In-Room Dining", "ird", "in_room_dining")}
              {renderRow("Total IRD Revenue", "ird", null, "total")}

              {renderRow("BANQUET REVENUE", null, null, "section")}
              {renderRow("Banquet", "banquet", "banquet")}
              {renderRow("Total Banquet Revenue", "banquet", null, "total")}

              {renderRow("PLAN REVENUE", null, null, "section")}
              {renderRow("Plan Food", "plan", "plan_food")}
              {renderRow("Total Plan Revenue", "plan", null, "total")}

              <TableRow className="bg-primary/15 font-medium" data-testid="innkey-row-total-fb">
                <TableCell className="text-primary">Total F&amp;B Service</TableCell>
                {cols.map((c) => <TableCell key={c.key} className="text-right font-mono-plex text-primary">{inr(data?.[c.key]?.total_fb)}</TableCell>)}
              </TableRow>

              {renderRow("Front Office", null, null, "section")}
              {renderRow("ROOM REVENUE", null, null, "section")}
              {renderRow("Room Revenue", "room", "room_revenue")}
              {renderRow("Total Room Revenue", "room", null, "total")}

              {renderRow("OTHER REVENUE", null, null, "section")}
              {renderRow("Misc Revenue", "other", "misc_revenue")}
              {renderRow("Total Other Revenue", "other", null, "total")}

              <TableRow className="bg-primary/15 font-medium" data-testid="innkey-row-total-fo">
                <TableCell className="text-primary">Total Front Office</TableCell>
                {cols.map((c) => <TableCell key={c.key} className="text-right font-mono-plex text-primary">{inr(data?.[c.key]?.total_front_office)}</TableCell>)}
              </TableRow>

              <TableRow className="bg-primary text-primary-foreground text-base" data-testid="innkey-row-grand">
                <TableCell className="font-medium">Grand Total</TableCell>
                {cols.map((c) => <TableCell key={c.key} className="text-right font-mono-plex font-medium">{inr(data?.[c.key]?.grand_total)}</TableCell>)}
              </TableRow>
            </TableBody>
          </Table>

          {/* Statistics side */}
          <div className="border-t border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-[hsl(var(--secondary))]/20">
                  <TableHead className="w-64">Statistics</TableHead>
                  {cols.map((c) => <TableHead key={c.key} className="text-right">{c.label}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  ["Arrivals", "arrivals"],
                  ["Walk-ins", "walk_ins"],
                  ["Departures", "departures"],
                  ["No-shows", "no_shows"],
                  ["Room nights sold", "room_nights_sold"],
                  ["Guests in-house", "guests_in_house"],
                  ["Occupancy %", "occupancy_pct", (v) => `${v ?? 0}%`],
                  ["ADR", "adr", (v) => inr(v)],
                  ["RevPAR", "revpar", (v) => inr(v)],
                ].map(([label, key, fmt]) => (
                  <TableRow key={key} data-testid={`innkey-stat-${key}`}>
                    <TableCell className="pl-6">{label}</TableCell>
                    {cols.map((c) => {
                      const v = data?.[c.key]?.stats?.[key];
                      const display = fmt ? fmt(v) : (v ?? 0);
                      return <TableCell key={c.key} className="text-right font-mono-plex">{display}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </Card>
  );
}

function buildInnkeyHtml(d, activeName) {
  const _e = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const money = (v) => (v == null ? "—" : "₹" + Number(v || 0).toLocaleString("en-IN"));
  const cols = ["yesterday", "wtd", "mtd", "ytd"];
  const labels = ["Yesterday", "Week To Date", "Month To Date", "Year To Date"];
  const val = (col, group, path) => {
    const b = d?.[col]?.[group];
    if (!b) return null;
    return path ? b[path] : b.total;
  };
  const dataRow = (label, group, path, kind) => {
    const isTot = kind === "total";
    const bg = isTot ? "background:#F5F1E6;font-weight:600" : "";
    return `<tr style="${bg}"><td style="padding-left:${isTot ? 12 : 24}px">${_e(label)}</td>${cols.map((c) => `<td class="r">${money(val(c, group, path))}</td>`).join("")}</tr>`;
  };
  const sectionRow = (label) => `<tr style="background:#2F5D50;color:#fff"><td colspan="5" style="padding:6px 10px;text-transform:uppercase;letter-spacing:.08em;font-size:11px">${_e(label)}</td></tr>`;
  const bigTot = (label, key) => `<tr style="background:#C8A24B;color:#16241B;font-weight:700"><td>${_e(label)}</td>${cols.map((c) => `<td class="r">${money(d?.[c]?.[key])}</td>`).join("")}</tr>`;
  const grandTot = () => `<tr style="background:#2F5D50;color:#fff;font-weight:700"><td>Grand Total</td>${cols.map((c) => `<td class="r">${money(d?.[c]?.grand_total)}</td>`).join("")}</tr>`;

  return `<!doctype html><html><head><title>Manager's Report · ${_e(activeName || d.property_name || "")} · ${_e(d.as_of)}</title><meta charset="utf-8"/><style>
    body{font-family:'Inter',system-ui,sans-serif;color:#16241B;margin:20px;font-size:11px}
    h1{font-family:'Playfair Display',serif;color:#2F5D50;margin:0;font-size:20px}
    .meta{color:#4E534D;font-size:10px;margin:4px 0 12px}
    table{width:100%;border-collapse:collapse;font-size:10.5px}
    th,td{padding:5px 8px;border-bottom:1px solid #E5E5DE;text-align:left}
    th{background:#F5F1E6;font-size:10px;text-transform:uppercase;letter-spacing:.06em}
    td.r,th.r{text-align:right;font-family:'JetBrains Mono',monospace}
    @page{margin:12mm}
  </style></head><body>
  <h1>${_e(activeName || d.property_name || "Property")}</h1>
  <div class="meta">Manager&#39;s Report · Revenue Matrix · As of ${_e(d.as_of)}</div>
  <table>
    <tr><th>Head</th>${labels.map((l) => `<th class="r">${_e(l)}</th>`).join("")}</tr>
    ${sectionRow("F&B Service")}
    <tr><td colspan="5" style="padding:4px 12px;font-weight:600">Restaurant Revenue</td></tr>
    ${dataRow("Food Revenue", "restaurant", "food")}
    ${dataRow("Bar", "restaurant", "bar")}
    ${dataRow("Total Restaurant Revenue", "restaurant", null, "total")}
    <tr><td colspan="5" style="padding:4px 12px;font-weight:600">IRD Revenue</td></tr>
    ${dataRow("In-Room Dining", "ird", "in_room_dining")}
    ${dataRow("Total IRD Revenue", "ird", null, "total")}
    <tr><td colspan="5" style="padding:4px 12px;font-weight:600">Banquet Revenue</td></tr>
    ${dataRow("Banquet", "banquet", "banquet")}
    ${dataRow("Total Banquet Revenue", "banquet", null, "total")}
    <tr><td colspan="5" style="padding:4px 12px;font-weight:600">Plan Revenue</td></tr>
    ${dataRow("Plan Food", "plan", "plan_food")}
    ${dataRow("Total Plan Revenue", "plan", null, "total")}
    ${bigTot("Total F&B Service", "total_fb")}
    ${sectionRow("Front Office")}
    <tr><td colspan="5" style="padding:4px 12px;font-weight:600">Room Revenue</td></tr>
    ${dataRow("Room Revenue", "room", "room_revenue")}
    ${dataRow("Total Room Revenue", "room", null, "total")}
    <tr><td colspan="5" style="padding:4px 12px;font-weight:600">Other Revenue</td></tr>
    ${dataRow("Misc Revenue", "other", "misc_revenue")}
    ${dataRow("Total Other Revenue", "other", null, "total")}
    ${bigTot("Total Front Office", "total_front_office")}
    ${grandTot()}
  </table>
  <h2 style="font-family:'Playfair Display',serif;color:#2F5D50;font-size:13px;text-transform:uppercase;letter-spacing:.08em;border-bottom:1px solid #C8A24B;padding-bottom:3px;margin:18px 0 6px">Statistics</h2>
  <table>
    <tr><th>Metric</th>${labels.map((l) => `<th class="r">${_e(l)}</th>`).join("")}</tr>
    ${[
      ["Arrivals","arrivals",null],
      ["Walk-ins","walk_ins",null],
      ["Departures","departures",null],
      ["No-shows","no_shows",null],
      ["Room nights sold","room_nights_sold",null],
      ["Guests in-house","guests_in_house",null],
      ["Occupancy %","occupancy_pct","pct"],
      ["ADR","adr","money"],
      ["RevPAR","revpar","money"],
    ].map(([label, key, fmt]) => `<tr><td>${_e(label)}</td>${cols.map((c) => {
      const v = d?.[c]?.stats?.[key];
      const disp = fmt === "money" ? money(v) : fmt === "pct" ? `${v ?? 0}%` : (v ?? 0);
      return `<td class="r">${disp}</td>`;
    }).join("")}</tr>`).join("")}
  </table>
  <p style="margin-top:18px;text-align:right;color:#4E534D;font-size:9px;text-transform:uppercase;letter-spacing:.05em">Printed ${_e(new Date().toLocaleString("en-IN"))}</p>
  <script>window.addEventListener('load', () => setTimeout(() => { window.focus(); window.print(); }, 300));</script>
  </body></html>`;
}

